import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import '../style/Sidebar.css';
import { useSidebarCounts } from '../hooks/useSidebarCounts';

// ✅ Import default icons (non-active)
import dashboardIcon from "../assets/icon/dashboard.png";
import campaignIcon from "../assets/icon/campaign.png";
import depositIcon from "../assets/icon/deposit.png";
import referralIcon from "../assets/icon/referral.png";
import supportIcon from "../assets/icon/contact.png";
import userManagementIcon from "../assets/icon/user-management.png";
import referralSettingIcon from "../assets/icon/referral-setting.png";
import notifIcon from "../assets/icon/notif.png";

// ✅ Import active icons (for active menu state)
import dashboardActive from "../assets/icon/dashboard-white.png";
import campaignActive from "../assets/icon/campaign-white.png";
import depositActive from "../assets/icon/deposit-white.png";
import referralActive from "../assets/icon/referral-white.png";
import supportActive from "../assets/icon/contact-white.png";
import userManagementActive from "../assets/icon/user-management-white.png";
import referralSettingActive from "../assets/icon/referral-setting-white.png";
import notifActive from "../assets/icon/notif-white.png";

const Sidebar = ({ isOpen, onClose, isCollapsed }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [role, setRole] = useState("user");
  const [hoveredIndex, setHoveredIndex] = useState(null); // <-- untuk deteksi hover icon
  const counts = useSidebarCounts();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.role) {
          setRole(parsedUser.role);
        }
      } catch (err) {
        console.error("Error parsing localStorage user:", err);
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.sidebar')) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, isMobile]);

  // =======================
  // USER MENU (image icons)
  // =======================
  const userMenu = [
    { path: "/dashboard", label: "Dashboard", icon: dashboardIcon, activeIcon: dashboardActive },
    { path: "/campaign", label: "Campaign", icon: campaignIcon, activeIcon: campaignActive },
    { path: "/deposits/list", label: "Deposit", icon: depositIcon, activeIcon: depositActive },
    { path: "/referral", label: "Referral", icon: referralIcon, activeIcon: referralActive },
    { path: "/contact/user", label: "Ticket Support", icon: supportIcon, activeIcon: supportActive },
  ];

  // =======================
  // ADMIN MENU (emoji icons)
  // =======================
  const adminMenu = [
    { path: "/dashboard", label: "Dashboard", icon: dashboardIcon, activeIcon: dashboardActive, count: 0 },
    { path: "/admin/user/list", label: "User Management", icon: userManagementIcon, activeIcon: userManagementActive, count: counts.userManagement },
    { path: "/campaign", label: "Campaign Management", icon: campaignIcon, activeIcon: campaignActive, count: counts.campaignManagement },
    { path: "/admin/deposits/list", label: "Deposit Management", icon: depositIcon, activeIcon: depositActive, count: counts.depositManagement },
    { path: "/admin/referral/list", label: "Referral Settings", icon: referralSettingIcon, activeIcon: referralSettingActive, count: 0 },
    { path: "/admin/notifications", label: "Verification Requests", icon: notifIcon, activeIcon: notifActive, count: counts.notificationManagement },
    { path: "/contact/admin", label: "Ticket Support", icon: supportIcon, activeIcon: supportActive, count: counts.ticketSupport },
  ];

  const menuList = role === "admin" ? adminMenu : userMenu;

  // =======================
  // RENDER SIDEBAR
  // =======================
  return (
    <>
      {isMobile && isOpen && (
        <div className="sidebar-overlay active" onClick={onClose} />
      )}

      <nav className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <ul className="menu-sidebar">
          {menuList.map((item, index) => (
            <li
              key={index}
              className="menu-item-sidebar"
              onMouseEnter={() => setHoveredIndex(index)}  // <-- hover start
              onMouseLeave={() => setHoveredIndex(null)}   // <-- hover end
            >
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `menu-link ${isActive ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`
                }
                onClick={isMobile ? onClose : null}
                title={item.description || item.label}
              >
                {({ isActive }) => (
                  <>
                    <span className="icon-wrappers">
                      {item.icon && item.activeIcon ? (
                        <img
                          src={
                            isActive || hoveredIndex === index
                              ? item.activeIcon
                              : item.icon
                          }
                          alt={item.label}
                          className="menu-icon-img"
                        />
                      ) : (
                        <span className="emoji-icon">{item.icon}</span>
                      )}
                    </span>

                    {!isCollapsed && (
                      <span className="menu-content">
                        <span className="menu-label">{item.label}</span>
                        {role === "admin" && item.count > 0 && (
                          <span className="menu-count">
                            {item.count > 99 ? "99+" : item.count}
                          </span>
                        )}
                      </span>
                    )}

                    {isCollapsed && role === "admin" && item.count > 0 && (
                      <span className="menu-count-collapsed">
                        {item.count > 9 ? "9+" : item.count}
                      </span>
                    )}
                  </>
                )}
              </NavLink>

              {isCollapsed && (
                <div className="menu-tooltip">
                  {item.label}
                  {item.count > 0 && ` (${item.count} new)`}
                </div>
              )}
            </li>
          ))}
        </ul>

        {role === "admin" && !isCollapsed && (
          <div className="sidebar-footer">
            <small>Last update: {new Date().toLocaleTimeString()}</small>
          </div>
        )}
      </nav>
    </>
  );
};

export default Sidebar;
